import { useEffect, useRef } from 'react';

/** True when the current browser tab is hidden (e.g. backgrounded/minimized). */
function isTabHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * Poll a callback at a fixed interval.
 *
 * - Runs once immediately on mount, then on an interval.
 * - Skips a tick while a previous invocation is still resolving, so slow
 *   responses never stack into overlapping requests.
 * - Pauses while the tab is hidden and refires as soon as it becomes visible.
 * - Adds random jitter around the interval so many devices/tabs don't hit the
 *   server on the exact same tick.
 *
 * @param {() => void} callback
 * @param {{ intervalMs?: number, enabled?: boolean, pauseWhenHidden?: boolean }} [opts]
 */
export function usePolling(callback, opts = {}) {
  const { intervalMs = 20000, enabled = true, pauseWhenHidden = true } = opts;

  const started = useRef(false);
  const inFlight = useRef(false);
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let timer;

    const run = async () => {
      if (inFlight.current) return; // previous call still resolving — skip this tick
      if (pauseWhenHidden && isTabHidden()) return; // backgrounded tab — hold off
      inFlight.current = true;
      try {
        await cbRef.current();
      } catch {
        /* swallow — the refresh callback already reports errors */
      } finally {
        inFlight.current = false;
      }
    };

    // Fire once immediately on mount (deduped across effect re-runs).
    if (!started.current) {
      started.current = true;
      run();
    }

    const schedule = () => {
      // Jitter ±12.5% so many open clients don't sync up to one shared tick.
      const jitter = intervalMs > 6000 ? Math.round((Math.random() - 0.5) * intervalMs * 0.25) : 0;
      timer = setTimeout(run, Math.max(1000, intervalMs + jitter));
    };
    schedule();

    // Resume promptly whenever a hidden tab comes back into view.
    const onVisibilityChange = () => {
      if (!isTabHidden()) run();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled, pauseWhenHidden]);
}